import MessageHelper from '../../helpers/message-helper';
import StockForecast from '../../model/forecast-stock-data';
import Tooltip, { TippyHTMLElement } from '../shared/tooltip';
import PageHelper from '../../helpers/page-helper';
import AvailabilityStatusIcon from './availability-status-icon';
import ProductFlags from '../../model/product-flags';

type RenderType = 'backorder' | 'full' | 'reduced';

export default class DeliveryAvailability {
  static readonly ASYNC_KEY = 'deliveryAvailability';

  private readonly ICON_ARROW = `<img class="icon -small" 
    src="${window.ACC.config.commonResourcePath}/images/icons/circle-w-arrow.png" alt=""/>`;

  private readonly ICON_CHECK = `<img class="icon -small" 
    src="${window.ACC.config.commonResourcePath}/images/icons/check-circle-green.png" alt=""/>`;

  private readonly ICON_CHECK_GREY = `<img class="icon -small" 
    src="${window.ACC.config.commonResourcePath}/images/icons/check-circle-grey.png" alt=""/>`;

  private readonly ICON_VAN = `<img class="icon -small -is-van" 
    src="${window.ACC.config.commonResourcePath}/images/icons/van.png" alt=""/>`;

  private readonly ICON_ALERT = `<img class="icon -small" 
    src="${window.ACC.config.commonResourcePath}/images/icons/alert-round-orange-bold.png" alt=""/>`;

  private readonly estimatedDeliveryDateFormatted: string;
  private readonly showStatusIcon: boolean;
  private readonly isInitialOrder: boolean = PageHelper.isInitialOrderPage();
  private readonly isWeekDisplay: boolean;
  private readonly isPhasedOut: boolean;
  private readonly deliveryData?: StockForecast[];

  constructor(private container: HTMLElement, deliveryData?: StockForecast[], flags?: ProductFlags) {
    const {
      dateFormat, estimatedDeliveryDateFormatted, variant, forecastJson, isPhasedOut,
    } = this.container.dataset;

    this.estimatedDeliveryDateFormatted = estimatedDeliveryDateFormatted ?? '';
    this.showStatusIcon = variant !== 'no-icon';
    this.isWeekDisplay = dateFormat === 'week';
    this.isPhasedOut = flags?.isPhasedOut ?? isPhasedOut === 'true' ?? false;

    if (!deliveryData && forecastJson) {
      try {
        // eslint-disable-next-line no-param-reassign
        deliveryData = JSON.parse(forecastJson) as StockForecast[];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    if (deliveryData) {
      this.deliveryData = this.isWeekDisplay ? this.groupByCalendarWeek(deliveryData) : deliveryData;
    }

    this.render();
  }

  static initAll(): DeliveryAvailability[] {
    return Array.from(
      document.querySelectorAll('.js-deliveryAvailability'),
    ).map((el) => new DeliveryAvailability(el as HTMLElement));
  }

  destroyTooltipInstance():void {
    // eslint-disable-next-line no-underscore-dangle
    (this.container as TippyHTMLElement)._tippy?.destroy();
  }

  initTooltip(content = ''): Tooltip {
    this.destroyTooltipInstance();

    return new Tooltip(this.container, content, {
      interactive: true,
      trigger: 'mouseenter click',
      delay: [2000, 0],
      placement: 'top-end',
      theme: 'eparts',
      onTrigger(instance, event) {
        // show the tooltip on click immediately, do not wait for delay to pass
        if (event.type === 'click') {
          instance.show();
        }
      },
    });
  }

  render(): void {
    const renderAs = this.getRenderType();

    let tooltipContent = '';
    let multipleForecasts = false;
    let unknownForecast = false;

    if (this.deliveryData && this.deliveryData.length) {
      multipleForecasts = this.deliveryData.length > 1;
      this.destroyTooltipInstance();
      this.container.classList.remove('has-more-info');

      const {
        quantity, dateFormatted, calendarWeek,
      } = this.deliveryData[0];
      unknownForecast = this.deliveryData[0].unknownForecast;
      const isHidden = unknownForecast && this.isPhasedOut;

      const listElements = this.renderDeliveryList(renderAs, this.deliveryData);
      let articleTemplate = this.renderAvailabilityTemplate(listElements, renderAs);

      if (renderAs === 'reduced' || renderAs === 'backorder') {
        let content: string;

        // In Initial Order, we never show that delivery date is unknown. It will be
        // shown as delivered by the default value of calendarWeek
        if (isHidden) {
          // show nothing
          content = '';
          articleTemplate = '';
        } else if (unknownForecast && !this.isInitialOrder) {
          const messageKey = renderAs === 'backorder' ? 'common.stock.unknownDate' : 'eparts.product.deliveryAvailability.unknownDelivery';
          content = `${MessageHelper.getMessageWithAttributes(
            messageKey,
            quantity.toString(),
          )} ${renderAs !== 'backorder' ? this.ICON_ARROW : ''}`;
        } else {
          const arrivingByMsgCode = this.getMessageKey(quantity, unknownForecast);

          let arrivalDate: string = dateFormatted;
          if (this.isWeekDisplay) {
            arrivalDate = `${calendarWeek}`;
          }

          const statusIcon = AvailabilityStatusIcon.getIcon(this.deliveryData);
          const messageFormatted = MessageHelper.getMessageWithAttributes(arrivingByMsgCode, quantity.toString(), arrivalDate);

          content = `${this.showStatusIcon ? statusIcon : ''}<span>${messageFormatted}</span>${(multipleForecasts ? this.ICON_ARROW : '')}`;
        }
        this.container.innerHTML = content;

        tooltipContent = articleTemplate;
      }

      if (renderAs === 'full' && !isHidden) {
        this.container.innerHTML = `<div class="box -with-border -with-background -rounded w-100">${articleTemplate}</div>`;
      }
    } else if (renderAs === 'backorder') {
      // only for order lists to display estimated delivery date from SalesOrder-Search
      this.container.innerHTML = this.estimatedDeliveryDateFormatted;
    }

    if ((renderAs === 'reduced' || renderAs === 'backorder') && (multipleForecasts || (unknownForecast && !this.isInitialOrder))) {
      this.initTooltip(tooltipContent);
      // add class for styling purposes only
      this.container.classList.add('has-more-info');
    }
  }

  renderDeliveryList(renderAs: string, forecasts: StockForecast[]): string {
    const listItems = forecasts.map((forecast) => {
      const { alternativeUrl } = this.container.dataset;
      const {
        quantity, dateFormatted, calendarWeek, unknownForecast,
      } = forecast;

      if (unknownForecast && !this.isInitialOrder) {
        const alternativeMessage = alternativeUrl ? MessageHelper.getMessageWithAttributes(
          'eparts.product.deliveryAvailability.alternatives',
          `${window.ACC.config.contextPath}${alternativeUrl}`,
        ) : '';
        const contactString = `${MessageHelper.getMessage('eparts.product.deliveryAvailability.contactInfo') || ''} ${alternativeMessage}`;
        const messageKey = renderAs === 'backorder' ? 'eparts.product.deliveryAvailability.itemsInClarification'
          : 'eparts.product.deliveryAvailability.itemsNotDeliverable';
        const notDeliverableMessage = MessageHelper.getMessageWithAttributes(
          `${messageKey}${quantity > 1 ? '.plural' : ''}`,
          quantity.toString(),
        );
        /* eslint-disable */
        return (`
          <li class="product-availability__list-item" data-delivery-list-item-undeliverable="">
            <div class="product-availability-non-deliverable-banner">
              ${this.ICON_ALERT}
              <div>
                <h3 class="product-availability-non-deliverable-banner__title">${notDeliverableMessage}</h3>
                ${renderAs !== 'backorder' ? '<p>' + contactString + '</p>' : ''}
              </div>
            </div>
          </li>  
        `);
        /* eslint-enable */
      }

      const arrivingByMsgCode = this.getMessageKey(quantity, unknownForecast);

      let arrivalDate: string = dateFormatted;
      if (this.isWeekDisplay) {
        arrivalDate = `${calendarWeek}`;
        document.body.dispatchEvent(new CustomEvent('maxDeliveryAvailabilityWeek', {
          detail: { week: calendarWeek },
        }));
      }

      const icon = unknownForecast ? this.ICON_CHECK_GREY : this.ICON_CHECK;

      return (`
        <li class="product-availability__list-item">
        ${icon}<span>${MessageHelper.getMessageWithAttributes(
          arrivingByMsgCode,
          quantity.toString(),
          arrivalDate,
        )}</span>
        </li>  
      `);
    });

    return (`
      <ul class="product-availability__list">
        ${listItems.join('')}
      </ul>
    `);
  }

  renderAvailabilityTemplate(listElementsMarkup: string, renderType: RenderType): string {
    const cutoffTimeFormatted = this.deliveryData?.[0]?.cutoffTimeFormatted;
    const orderDeadline = cutoffTimeFormatted && renderType === 'full'
      ? `<span class="order-deadline -dark">${MessageHelper.getMessageWithAttributes('eparts.product.orderDeadline', cutoffTimeFormatted)}</span>`
      : '';

    return (`
      <article class="product-availability">
        <h2 class="product-availability__name">
          ${this.ICON_VAN}${MessageHelper.getMessage('eparts.product.deliveryAvailability.sectionTitle')}
          ${orderDeadline}
        </h2>
        ${listElementsMarkup}
      </article>
    `);
  }

  // eslint-disable-next-line class-methods-use-this
  private getMessageKey(quantity: number, isUnknownForecast: boolean): string {
    let formatKey = 'arrivingBy';

    if (this.isWeekDisplay) {
      // Unknown delivery week should only happen in Initial Order
      formatKey = (isUnknownForecast) ? 'unknownDeliveryWeek' : 'arrivingByWeek';
    }

    const suffix = quantity > 1 ? '.plural' : '';
    return `eparts.product.deliveryAvailability.${formatKey}${suffix}`;
  }

  private getRenderType(): RenderType {
    const { renderType } = this.container.dataset;
    if (!renderType || !['backorder', 'full', 'reduced'].includes(renderType)) {
      return 'reduced';
    }
    return renderType as RenderType;
  }

  private groupByCalendarWeek(items: StockForecast[]): StockForecast[] {
    const validItems = items.filter((item) => !item.unknownForecast);
    const unknownItems = items.filter((item) => item.unknownForecast);

    const grouped: Record<number, Partial<StockForecast>> = {};
    const order: number[] = [];

    validItems.forEach((item) => {
      const week = item.calendarWeek;

      if (!grouped[week]) {
        grouped[week] = {
          calendarWeek: week,
          quantity: 0,
          date: item.date,
          dateFormatted: item.dateFormatted,
          unknownForecast: false,
        };
        order.push(week);
      }

      // @ts-expect-error -- Definitely not undefined
      grouped[week].quantity += item.quantity;
    });

    const groupedArray = order.map((week) => grouped[week]);

    return [...groupedArray, ...unknownItems].map((item, index) => ({
      ...item,
      index: index + 1,
    })) as StockForecast[];
  }
}
